import React, { ChangeEvent, useState, useEffect } from "react";
import styled from "styled-components";
import InputField from "components/InputField";
import { designTokens, getTypography } from "styles/designTokens";

interface EmailStepProps {
  email: string;
  setEmail: (value: string) => void;
  verificationCode: string;
  setVerificationCode: (value: string) => void;
  error: string | null;
  verificationCodeError: string | null;
  onSendCode: () => void;
  codeSendCount: number;
  isEmailDisabled: boolean;
  onVerifyCode?: () => Promise<boolean>;
  isEmailVerified?: boolean;
}

const HorizontalContainer = styled.div`
  display: flex;
  align-items: start;
  gap: 8px;
  width: 100%;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  flex-grow: 1;
`;

const ActionButton = styled.button<{ disabled?: boolean }>`
  padding: 8px 16px;
  height: 42px;
  border-radius: 6px;
  border: 1px solid ${designTokens.colors.line.button};
  background: ${({ disabled }) => (disabled ? "#f5f5f5" : "white")};
  color: ${({ disabled }) => (disabled ? "#aaa" : designTokens.colors.font.primary)};
  ${getTypography("korean", "body3")}
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  white-space: nowrap;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background-color: #f8f9fa;
  }
`;


const EmailStep: React.FC<EmailStepProps> = ({
  email,
  setEmail,
  verificationCode,
  setVerificationCode,
  error,
  verificationCodeError,
  onSendCode,
  codeSendCount,
  isEmailDisabled,
  onVerifyCode,
  isEmailVerified = false,
}) => {
  const [timeLeft, setTimeLeft] = useState(0);
  const isCodeSent = codeSendCount > 0;

  useEffect(() => {
    if (codeSendCount > 0 && !isEmailVerified) {
      setTimeLeft(300); // Start/reset timer when code is sent
    }
  }, [codeSendCount, isEmailVerified]);

  // 인증 완료 시 타이머 즉시 중지
  useEffect(() => {
    if (isEmailVerified) {
      setTimeLeft(0);
    }
  }, [isEmailVerified]);

  useEffect(() => {
    if (timeLeft <= 0 || isEmailVerified) return; // 인증 완료 시 타이머 중지
    const intervalId = setInterval(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [timeLeft, isEmailVerified]);

  const handleSendCodeClick = () => {
    onSendCode();
  };

  const handleVerifyCodeClick = async () => {
    if (onVerifyCode) {
      await onVerifyCode();
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <>
      <InputField
        label="이메일"
        type="email"
        value={email}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setEmail(e.target.value)
        }
        placeholder="이메일"
        error={error || undefined}
        required
        disabled={isEmailDisabled || isEmailVerified}
      />

      <HorizontalContainer>
        <InputWrapper>
          <InputField
            label="인증코드"
            type="text"
            value={verificationCode}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setVerificationCode(e.target.value)
            }
            placeholder="인증코드"
            disabled={!isCodeSent || isEmailVerified}
            error={verificationCodeError || undefined}
            timer={isCodeSent && timeLeft > 0 && !isEmailVerified ? formatTime(timeLeft) : undefined}
            required
          />
        </InputWrapper>
      
        {isCodeSent && !isEmailVerified && (
          <>
            <ActionButton onClick={handleSendCodeClick} type="button">
              재전송
            </ActionButton>
            <ActionButton 
              onClick={handleVerifyCodeClick} 
              type="button"
              disabled={!verificationCode.trim()}
            >
              인증 확인
            </ActionButton>
          </>
        )}
        
        {!isCodeSent && (
          <ActionButton onClick={handleSendCodeClick} type="button">
            코드 전송
          </ActionButton>
        )}
        
        {isEmailVerified && (
          <ActionButton disabled type="button">
            인증 완료
          </ActionButton>
        )}
      </HorizontalContainer>

    </>
  );
};

export default EmailStep;
